import liff from '@line/liff'
import { createFileRoute } from '@tanstack/react-router'
import { postApiV1WeddingsByWeddingIdLineChannelVerifyProviderPing } from '@wedding-tech/api-client'
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  UserPlus,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { z } from 'zod'

import { env } from '@/config'

const searchSchema = z.object({
  token: z.string().optional(),
  liffId: z.string().optional(),
  eventId: z.string().optional(),
})

export const Route = createFileRoute('/verify')({
  validateSearch: searchSchema,
  component: VerifyComponent,
})

type VerificationState =
  | 'initializing-liff'
  | 'checking-friendship'
  | 'not-friend'
  | 'verifying'
  | 'success'
  | 'error'

function VerifyComponent() {
  const { token, liffId, eventId } = Route.useSearch()

  const [state, setState] = useState<VerificationState>('initializing-liff')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [userInfo, setUserInfo] = useState<{
    userId: string
    displayName: string
    pictureUrl?: string
  } | null>(null)

  const isInit = useRef(false)

  const performPing = useCallback(
    async (targetEventId: string, verifyToken: string) => {
      try {
        setState('verifying')
        const accessToken = liff.getAccessToken()
        if (!accessToken) {
          throw new Error(
            'ไม่สามารถดึง LINE Access Token ได้ กรุณาลองใหม่อีกครั้ง',
          )
        }

        const response =
          await postApiV1WeddingsByWeddingIdLineChannelVerifyProviderPing({
            path: {
              weddingId: targetEventId,
            },
            body: {
              verifyToken,
              accessToken,
            },
          })

        const data = response.data

        if (!data?.success) {
          setState('error')
          setErrorMessage(data?.data?.error || 'การตรวจสอบยืนยันล้มเหลว')
          return
        }

        if (data.data?.providerMatch) {
          setUserInfo({
            userId: data.data.user?.userId || '',
            displayName: data.data.user?.displayName || 'LINE User',
            pictureUrl: data.data.user?.pictureUrl,
          })
          setState('success')
        } else {
          setState('error')
          setErrorMessage(
            data.data?.error ||
              'LINE User ID ไม่พบใน Messaging API / อยู่คนละ Provider',
          )
        }
      } catch (err: unknown) {
        setState('error')
        setErrorMessage(
          err instanceof Error
            ? err.message
            : 'เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์',
        )
      }
    },
    [],
  )

  const checkFriendshipAndPing = useCallback(
    async (targetEventId: string, verifyToken: string) => {
      try {
        setState('checking-friendship')
        const friendship = await liff.getFriendship()

        if (!friendship.friendFlag) {
          setState('not-friend')
          return
        }

        await performPing(targetEventId, verifyToken)
      } catch (err: unknown) {
        setState('error')
        setErrorMessage(
          err instanceof Error
            ? err.message
            : 'ไม่สามารถตรวจสอบสถานะการเป็นเพื่อนได้',
        )
      }
    },
    [performPing],
  )

  const initAndRun = useCallback(async () => {
    if (!token || !eventId) {
      setState('error')
      setErrorMessage(
        'ข้อมูลการยืนยันไม่ครบถ้วน กรุณาสแกน QR Code จากหน้าตั้งค่า LINE ในระบบ Admin ใหม่ (ต้องการ token และ eventId)',
      )
      return
    }

    try {
      setState('initializing-liff')
      const targetLiffId = liffId || liff.id || env.EVT_DEFAULT_LIFF_ID
      if (targetLiffId) {
        await liff.init({ liffId: targetLiffId })
      }

      if (!liff.isLoggedIn()) {
        liff.login()
        return
      }

      await checkFriendshipAndPing(eventId, token)
    } catch (err: unknown) {
      setState('error')
      setErrorMessage(
        err instanceof Error
          ? err.message
          : 'เกิดข้อผิดพลาดในการเริ่มต้น LIFF SDK',
      )
    }
  }, [token, liffId, eventId, checkFriendshipAndPing])

  useEffect(() => {
    if (isInit.current) return
    isInit.current = true
    initAndRun()
  }, [initAndRun])

  const handleRequestFriendship = async () => {
    if (!token || !eventId) return
    try {
      await liff.requestFriendship()
      await checkFriendshipAndPing(eventId, token)
    } catch (err: unknown) {
      console.error('Request friendship error:', err)
    }
  }

  return (
    <div className="flex min-h-dvh w-full flex-col items-center justify-center bg-slate-50 p-6 text-slate-900">
      <div className="w-full max-w-sm rounded-2xl border border-slate-100 bg-white p-6 text-center shadow-lg">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
          <ShieldCheck className="h-8 w-8" />
        </div>

        <h1 className="mb-2 text-xl font-bold text-slate-800">
          LINE Provider Verification
        </h1>

        {state === 'initializing-liff' ||
        state === 'checking-friendship' ||
        state === 'verifying' ? (
          <div className="flex flex-col items-center justify-center space-y-3 py-6">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
            <p className="text-sm font-medium text-slate-500">
              {state === 'initializing-liff' && 'กำลังเริ่มต้น LINE LIFF...'}
              {state === 'checking-friendship' && 'กำลังตรวจสอบสถานะเพื่อน...'}
              {state === 'verifying' &&
                'กำลังส่งข้อมูลยืนยันไปยังเซิร์ฟเวอร์...'}
            </p>
          </div>
        ) : null}

        {state === 'not-friend' ? (
          <div className="space-y-4 py-4">
            <div className="rounded-lg bg-amber-50 p-4 text-left text-sm font-medium text-amber-800">
              กรุณาเพิ่มเพื่อนกับ Official Account เพื่อทำการยืนยัน
              Same-Provider Connection
            </div>

            <button
              onClick={handleRequestFriendship}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 font-semibold text-white transition hover:bg-emerald-700 active:scale-95"
            >
              <UserPlus className="h-5 w-5" />
              เพิ่มเพื่อนกับ Official Account
            </button>
          </div>
        ) : null}

        {state === 'success' ? (
          <div className="space-y-4 py-4">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <CheckCircle2 className="h-7 w-7" />
            </div>

            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center text-emerald-900">
              <p className="text-base font-bold">การยืนยันสำเร็จ!</p>
              <p className="mt-1 text-xs text-emerald-700">
                MINI App และ Messaging API อยู่ใน Provider เดียวกัน
              </p>
            </div>

            {userInfo ? (
              <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-left">
                {userInfo.pictureUrl ? (
                  <img
                    src={userInfo.pictureUrl}
                    alt={userInfo.displayName}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-200 font-bold text-slate-600">
                    {userInfo.displayName.charAt(0)}
                  </div>
                )}
                <div className="overflow-hidden">
                  <p className="truncate text-sm font-semibold text-slate-800">
                    {userInfo.displayName}
                  </p>
                  <p className="truncate text-xs text-slate-400">
                    ID: {userInfo.userId}
                  </p>
                </div>
              </div>
            ) : null}

            <button
              onClick={() => liff.closeWindow()}
              className="w-full rounded-xl bg-slate-800 px-4 py-3 font-medium text-white transition hover:bg-slate-900"
            >
              ปิดหน้าต่าง
            </button>
          </div>
        ) : null}

        {state === 'error' ? (
          <div className="space-y-4 py-4">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
              <AlertCircle className="h-7 w-7" />
            </div>

            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center text-sm font-medium text-red-900">
              {errorMessage}
            </div>

            <button
              onClick={() => liff.closeWindow()}
              className="w-full rounded-xl bg-slate-800 px-4 py-3 font-medium text-white transition hover:bg-slate-900"
            >
              ปิดหน้าต่าง
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
